/* Metta's optional discount control. Cart prices always come from Shopify's
   rendered sections; use Stiletto's existing section lifecycle and controls. */
(() => {
  if (customElements.get('metta-cart-discount')) return;
  let updating = false;
  const normalize = value => String(value || '').trim().toLocaleLowerCase();
  function appliedCodes(cart) {
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.filter(code => code.applicable !== false).map(code => code.code);
    }
    const applications = [...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application))];
    return [...new Set(applications.filter(a => a?.type === 'discount_code').map(a => a.title))];
  }
  function nextCodes(cart, code, remove) {
    const existing = appliedCodes(cart).filter(value => normalize(value) !== normalize(code));
    return remove ? existing : [...existing, code];
  }
  async function request(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }
  function renderSections(cart, instances) {
    // Validate every section before changing the visible cart.
    const sources = instances.map(instance => {
      const html = cart.sections?.[instance.id];
      if (!html) throw new Error('Missing cart section');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const type = instance.container.dataset.sectionType;
      const source = doc.querySelector(`[data-section-type="${type}"]`);
      if (!source) throw new Error('Missing cart contents');
      return { instance, source, html, type };
    });
    for (const { instance, source, html, type } of sources) {
      const note = instance.container.querySelector('textarea[name="note"]')?.value;
      if (type === 'quick-cart') {
        instance._renderQuickCart(html);
        // Native quantity refreshes keep the footer; update its persistent
        // discount list and code chips without replacing the focused control.
        const target = instance.container.querySelector('.quick-cart__item-discounts');
        const discounts = source.querySelector('.quick-cart__item-discounts');
        if (target && discounts) target.innerHTML = discounts.innerHTML;
      } else {
        instance.onUnload();
        instance.container.innerHTML = source.innerHTML;
        instance.onLoad();
      }
      if (note !== undefined) {
        const input = instance.container.querySelector('textarea[name="note"]');
        if (input) input.value = note;
      }
      instance.adjustItemPadding?.();
    }
    document.querySelectorAll('metta-cart-discount').forEach(control => control.showCodes(appliedCodes(cart)));
  }
  class MettaCartDiscount extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.addEventListener('click', this.onClick = event => {
        const apply = event.target.closest('[data-code-apply]');
        const remove = event.target.closest('[data-remove-code]');
        if (!apply && !remove) return;
        event.preventDefault();
        this.apply(remove?.dataset.removeCode, remove || apply);
      });
      this.addEventListener('keydown', this.onKey = event => {
        if (event.key === 'Enter' && event.target.matches('[data-code-input]')) {
          event.preventDefault();
          event.stopPropagation();
          this.apply(undefined, this.querySelector('[data-code-apply]'));
        }
      });
      this.footer = this.closest('.quick-cart__footer');
      if (this.footer) {
        this.observer = new ResizeObserver(() => {
          const items = this.closest('.quick-cart')?.querySelector('.quick-cart__items');
          if (items) items.style.paddingBottom = `${this.footer.clientHeight}px`;
        });
        this.observer.observe(this.footer);
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      this.connected = false;
    }
    showMessage(key, error = false) {
      const message = this.querySelector('[data-code-message]');
      message.textContent = this.dataset[key];
      message.dataset.error = String(error);
      message.hidden = false;
      this.querySelector('[data-code-input]').setAttribute('aria-invalid', String(error));
    }
    showCodes(codes) {
      const container = this.querySelector('[data-applied-codes]');
      container.replaceChildren(...codes.map(code => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'metta-cart-discount__code';
        button.dataset.removeCode = code;
        button.setAttribute('aria-label', `${this.dataset.removeLabel} ${code}`);
        button.append(document.createTextNode(code));
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '×';
        button.append(icon);
        return button;
      }));
    }
    async apply(removeCode, pressed) {
      if (updating) return;
      const input = this.querySelector('[data-code-input]');
      const code = String(removeCode ?? input.value).trim();
      if (!code || code.includes(',')) {
        this.showMessage('empty', true);
        input.focus();
        return;
      }
      const instances = (window.Shopify?.theme?.sections?.instances || []).filter(instance =>
        ['quick-cart', 'cart'].includes(instance.container?.dataset.sectionType));
      if (!instances.length || instances.some(instance =>
        instance.container.dataset.sectionType === 'quick-cart' && !instance._renderQuickCart)) {
        this.showMessage('error', true);
        return;
      }
      updating = true;
      const sectionID = this.dataset.section;
      const root = window.Shopify?.routes?.root || '/';
      const controls = Array.from(document.querySelectorAll('.quick-cart button:not(:disabled), .quick-cart input:not(:disabled), .metta-cart-page button:not(:disabled), .metta-cart-page input:not(:disabled)'));
      controls.forEach(control => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      // What was pressed shows it is working (metta-motion.css, LOADING
      // STATES): "Nota" takes the bar, a code being removed dims.
      pressed?.setAttribute('aria-busy', 'true');
      let changed = false;
      try {
        const previous = await request(`${root}cart.js`);
        const cart = await request(`${root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: nextCodes(previous, code, !!removeCode).join(','),
            sections: instances.map(instance => instance.id), sections_url: window.location.pathname })
        });
        changed = true;
        renderSections(cart, instances);
        const target = Array.from(document.querySelectorAll('metta-cart-discount')).find(control => control.dataset.section === sectionID);
        if (target) {
          const applied = appliedCodes(cart).some(value => normalize(value) === normalize(code));
          const ok = removeCode ? !applied : applied;
          const field = target.querySelector('[data-code-input]');
          target.querySelector('details').open = true;
          field.value = ok ? '' : code;
          target.showMessage(ok ? (removeCode ? 'removed' : 'applied') : 'invalid', !ok);
          field.disabled = false;
          field.focus({ preventScroll: true });
        }
      } catch (error) {
        const target = Array.from(document.querySelectorAll('metta-cart-discount')).find(control => control.dataset.section === sectionID) || this;
        target.showMessage(changed ? 'refreshError' : 'error', true);
      } finally {
        controls.forEach(control => { control.disabled = false; });
        document.querySelectorAll('metta-cart-discount').forEach(control => control.removeAttribute('aria-busy'));
        pressed?.removeAttribute('aria-busy');
        updating = false;
      }
    }
  }
  customElements.define('metta-cart-discount', MettaCartDiscount);
})();

/* Discount code and order note open the same way: the panel grows to its
   height and fades in (250ms, the site easing), and closes back. Opening one
   closes the other, so the pinned footer never stacks two boxes, and nothing
   scrolls the drawer to a field. Stiletto's note toggle (CartNoteToggle) slid
   the note on its own timing; it is bypassed here, its label swap kept.
   Suggestions under the drawer's items: adding one keeps the drawer where it
   is (theme.js skips its scroll to the top when the form carries
   data-metta-keep-scroll), and the new line flashes once. */
(() => {
  if (window.mettaCartDisclosure || typeof document.addEventListener !== 'function') return;
  window.mettaCartDisclosure = true;
  const animations = new WeakMap();
  const matches = query => window.matchMedia?.(query).matches;
  function region(trigger) {
    if (trigger.matches('summary')) {
      const details = trigger.parentElement;
      const panel = details.querySelector('[data-metta-disclosure-panel]');
      if (!panel) return null;
      return {
        root: details, panel, input: panel.querySelector('input'),
        isOpen: () => details.open && !details.hasAttribute('data-metta-closing'),
        show() { details.open = true; },
        hide() { details.open = false; }
      };
    }
    const wrapper = trigger.parentNode.querySelector('[cart-note-input]');
    if (!wrapper) return null;
    const label = trigger.querySelector('[data-cart-note-trigger-text]');
    const input = wrapper.querySelector('textarea');
    return {
      root: wrapper, panel: wrapper, input,
      isOpen: () => wrapper.getAttribute('aria-expanded') === 'true',
      show() {
        wrapper.setAttribute('aria-expanded', 'true');
        wrapper.setAttribute('aria-hidden', 'false');
      },
      hide() {},
      closing() {
        wrapper.setAttribute('aria-expanded', 'false');
        wrapper.setAttribute('aria-hidden', 'true');
        const strings = window.theme?.strings?.cart;
        if (label && strings) label.innerText = input?.value ? strings.editCartNote : strings.addCartNote;
      }
    };
  }
  function setOpen(r, open) {
    const { root, panel } = r;
    // A closed <details> still lays its content out, so measure only a panel
    // that is showing (open, or part way through closing).
    const showing = r.isOpen() || root.hasAttribute('data-metta-closing');
    const from = showing ? panel.getBoundingClientRect().height : 0;
    const opacity = showing ? Number(getComputedStyle(panel).opacity) : 0;
    animations.get(panel)?.cancel();
    if (open) {
      root.removeAttribute('data-metta-closing');
      r.show();
    } else {
      root.setAttribute('data-metta-closing', '');
      r.closing?.();
    }
    const finish = () => {
      panel.style.overflow = '';
      animations.delete(panel);
      if (open) return;
      root.removeAttribute('data-metta-closing');
      r.hide();
    };
    if (matches('(prefers-reduced-motion: reduce)') || typeof panel.animate !== 'function') return finish();
    panel.style.overflow = 'hidden';
    const to = open ? panel.scrollHeight : 0;
    const easing = getComputedStyle(document.documentElement).getPropertyValue('--easing').trim() || 'ease';
    const animation = panel.animate(
      [{ height: `${from}px`, opacity }, { height: `${to}px`, opacity: open ? 1 : 0 }],
      { duration: 250, easing }
    );
    animations.set(panel, animation);
    animation.onfinish = finish;
    // On a phone the keyboard would push the drawer; there the tap opens the
    // field and a second tap types.
    if (open && matches('(hover: hover) and (pointer: fine)')) r.input?.focus({ preventScroll: true });
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('.metta-cart-discount__details > summary, [data-order-note-trigger]');
    const footer = trigger?.closest('.quick-cart__footer-inner, .cart__footer-inner');
    const current = footer && region(trigger);
    if (!current) return;
    event.preventDefault();
    event.stopPropagation(); // CartNoteToggle would slide the note as well
    const open = !current.isOpen();
    if (open) {
      footer.querySelectorAll('.metta-cart-discount__details > summary, [data-order-note-trigger]').forEach(other => {
        const r = other !== trigger && region(other);
        if (r && r.isOpen()) setOpen(r, false);
      });
    }
    setOpen(current, open);
  }, true);

  let pending = null;
  const keys = form => new Set(Array.from(form.querySelectorAll('.quick-cart__item[data-key]'), item => item.dataset.key));
  document.addEventListener('click', event => {
    const add = event.target.closest?.('.quick-cart .cross-sells__item-add');
    const form = add?.closest('.quick-cart__form');
    const anchor = form?.querySelector('[data-cart-recommendations]');
    if (!anchor) return;
    form.setAttribute('data-metta-keep-scroll', '');
    pending = { form, anchor, keys: keys(form), top: anchor.getBoundingClientRect().top, until: Date.now() + 60000 };
    if (form.mettaKeepObserver) return;
    // Lines render above the suggestions; hold the suggestions where they
    // were so the new line pushes the list up out of view, not the view down.
    form.mettaKeepObserver = new MutationObserver(() => {
      if (!pending || pending.form !== form) return;
      if (Date.now() > pending.until || !form.isConnected) { pending = null; return; }
      const added = Array.from(form.querySelectorAll('.quick-cart__item[data-key]')).filter(item => !pending.keys.has(item.dataset.key));
      if (!added.length) return;
      form.scrollTop += pending.anchor.getBoundingClientRect().top - pending.top;
      added.forEach(item => item.classList.add('metta-cart-item--added'));
      pending = null;
    });
    form.mettaKeepObserver.observe(form, { childList: true, subtree: true });
  }, true);
})();
